import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ExportButton } from '@/components/common/ExportButton';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { ContradictionCard } from '@/pages/anatel/ContradictionCard';
import {
  anatelConsistencyParams,
  fetchConsistencyCitations,
  fetchConsistencyOverview,
  fetchContradictions,
} from '@/services/anatel';

const PAGE_SIZE = 10;

export function AnatelConsistencyPage() {
  const { t, i18n } = useTranslation(['anatel', 'common']);
  const [onlyVerified, setOnlyVerified] = useState(false);
  const [citation, setCitation] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);

  const overview = useQuery({
    queryKey: ['anatel', 'consistency'],
    queryFn: fetchConsistencyOverview,
  });

  const citations = useQuery({
    queryKey: ['anatel', 'consistency-citations'],
    queryFn: fetchConsistencyCitations,
    staleTime: Infinity,
  });

  const queue = useQuery({
    queryKey: ['anatel', 'contradictions', onlyVerified, citation, offset],
    queryFn: () =>
      fetchContradictions({
        onlyFullyVerified: onlyVerified,
        citation,
        limit: PAGE_SIZE,
        offset,
      }),
    placeholderData: keepPreviousData,
  });

  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('anatel:consistency.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('anatel:consistency.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="anatel:consistency.explainer" />

      <FieldGuide i18nKey="anatel:consistency.guide" level={2} />

      {overview.data?.as_of ? <AsOfStrip asOf={overview.data.as_of} /> : null}

      <div className="flex justify-end">
        {/* The export replays the screen's query -- filters included. */}
        <ExportButton
          path={`/api/v1/anatel/export/consistency?${anatelConsistencyParams({ onlyFullyVerified: onlyVerified, citation })}`}
        />
      </div>

      {overview.isPending ? (
        <SkeletonLoader rows={4} />
      ) : overview.error ? (
        <ErrorMessage message={overview.error.message} onRetry={() => void overview.refetch()} />
      ) : (
        <section className="rounded-lg border bg-card p-4">
          <Answer answer={overview.data.queue} help={t('anatel:consistency.help')}>
            {(value) => (
              <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Stat
                  label={t('anatel:consistency.flagged')}
                  value={number.format(value.contradictions)}
                  hint={t('anatel:consistency.ofAdjudicated', {
                    n: number.format(value.adjudicated),
                  })}
                />
                <Stat
                  label={t('anatel:consistency.fullyVerified')}
                  value={number.format(value.contradictions_fully_verified)}
                  hint={t('anatel:consistency.spansConfirmed')}
                />
                <Stat
                  label={t('anatel:consistency.adjudicated')}
                  value={percent.format(value.adjudicated_share)}
                  hint={t('anatel:consistency.ofPairs', {
                    n: number.format(value.different_outcome_pairs),
                  })}
                />
                <Stat
                  label={t('anatel:consistency.explained')}
                  value={number.format(
                    value.explanations.find((row) => row.code === 'FACTUAL_DISTINCTION')?.count ?? 0,
                  )}
                  hint={t('anatel:consistency.byFacts')}
                />
              </dl>
            )}
          </Answer>
        </section>
      )}

      {overview.data ? (
        <section className="rounded-lg border bg-card p-4">
          <h2 className="text-sm font-semibold text-foreground">
            {t('anatel:consistency.evidence.title')}
          </h2>
          <Answer answer={overview.data.evidence} help={t('anatel:consistency.evidence.help')}>
            {(value) => (
              <div className="mt-3 space-y-3">
                {/* Ordered safest-first by the analytics layer, so a reviewer
                    reads down into progressively weaker evidence. */}
                <ul className="space-y-1.5">
                  {value.states.map((state) => (
                    <li
                      key={state.status}
                      className="flex flex-wrap items-baseline justify-between gap-2 border-b pb-1.5 text-sm last:border-b-0"
                    >
                      <span className="text-foreground">
                        {/* Falls back to the raw code: the ETL owns this
                            vocabulary and can add to it, and a missing
                            translation must show the state rather than a key. */}
                        {t(`anatel:consistency.evidence.status.${state.status}`, {
                          defaultValue: state.status,
                        })}
                      </span>
                      <span className="tabular-nums text-muted-foreground">
                        {number.format(state.pairs)}
                        {state.worst_match_ratio !== null && state.worst_match_ratio < 1
                          ? ` · ${t('anatel:consistency.evidence.worstRatio', {
                              ratio: state.worst_match_ratio.toFixed(2),
                            })}`
                          : null}
                      </span>
                    </li>
                  ))}
                </ul>
                {/* Stated as its own figure rather than as a share of the
                    verified ones: verification and corroboration are different
                    questions, and 99 verbatim pairs include only 19 corroborated. */}
                <p className="text-xs text-muted-foreground">
                  {t('anatel:consistency.evidence.corroborated', {
                    n: number.format(value.both_propositions_corroborated),
                    reviewed: number.format(value.reviewed),
                    share: percent.format(value.corroborated_share),
                  })}
                </p>
              </div>
            )}
          </Answer>
        </section>
      ) : null}

      <QueueFilters
        onlyVerified={onlyVerified}
        citation={citation}
        citationOptions={citations.data ?? []}
        onOnlyVerified={(checked) => {
          setOnlyVerified(checked);
          setOffset(0);
        }}
        onCitation={(next) => {
          setCitation(next);
          setOffset(0);
        }}
      />

      {queue.isPending ? (
        <SkeletonLoader rows={10} />
      ) : queue.error ? (
        <ErrorMessage message={queue.error.message} onRetry={() => void queue.refetch()} />
      ) : queue.data.items.length === 0 ? (
        <EmptyState hint={t('anatel:consistency.emptyHint')} />
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground" aria-live="polite">
            {t('anatel:decisions.showing', {
              from: number.format(offset + 1),
              to: number.format(offset + queue.data.items.length),
              total: number.format(queue.data.total),
            })}
          </p>

          {queue.data.items.map((item) => (
            <ContradictionCard key={item.pair_key} item={item} />
          ))}

          <div className="flex items-center justify-between">
            <button
              type="button"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(offset - PAGE_SIZE, 0))}
              className="rounded-md border px-3 py-1.5 text-sm text-foreground disabled:opacity-40"
            >
              {t('anatel:decisions.previous')}
            </button>
            <button
              type="button"
              disabled={offset + PAGE_SIZE >= queue.data.total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
              className="rounded-md border px-3 py-1.5 text-sm text-foreground disabled:opacity-40"
            >
              {t('anatel:decisions.next')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function QueueFilters({
  onlyVerified,
  citation,
  citationOptions,
  onOnlyVerified,
  onCitation,
}: {
  onlyVerified: boolean;
  citation: string | null;
  citationOptions: string[];
  onOnlyVerified: (checked: boolean) => void;
  onCitation: (citation: string | null) => void;
}) {
  const { t } = useTranslation(['anatel']);
  return (
    <div className="flex flex-wrap items-center gap-4">
      <label className="flex items-center gap-2 text-sm text-foreground">
        <input
          type="checkbox"
          checked={onlyVerified}
          onChange={(event) => onOnlyVerified(event.target.checked)}
        />
        {t('anatel:consistency.onlyVerified')}
      </label>

      {/* Options are the grounds pairs in THIS queue actually share -- never
          the corpus vocabulary, which would offer grounds that select
          nothing. */}
      <label className="flex items-center gap-2 text-sm text-foreground">
        <span className="text-xs font-medium text-muted-foreground">
          {t('anatel:consistency.filters.citation')}
        </span>
        <select
          value={citation ?? ''}
          onChange={(event) => onCitation(event.target.value || null)}
          className="max-w-md rounded-md border bg-background px-2 py-1.5 text-xs text-foreground"
        >
          <option value="">{t('anatel:consistency.filters.citationAny')}</option>
          {citationOptions.map((ground) => (
            <option key={ground} value={ground}>
              {ground}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="rounded-md border bg-background p-3">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-xl font-semibold tabular-nums text-foreground">{value}</dd>
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}
